import React from 'react'
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { getBootstrap } from '../api'
import { getMe, type MeResponse } from '../api/me'
import PricingPublicPage from '../pages/pricing/PricingPublicPage'
import YoutubeCreditsPublicPage from '../pages/pricing/YoutubeCreditsPublicPage'

type PublicPage = 'pricing' | 'yt-credits'

const PublicApp: React.FC<{ page: PublicPage }> = ({ page }) => {
  const bootstrap = React.useMemo(() => {
    try {
      return getBootstrap()
    } catch {
      return null
    }
  }, [])
  const isLoggedIn = Boolean(bootstrap?.isLoggedIn)
  const [me, setMe] = React.useState<MeResponse | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!isLoggedIn) {
      setMe(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getMe()
      .then((data) => {
        if (cancelled) {
          return
        }
        setMe(data)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to load profile.'
        setError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isLoggedIn])

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading pricing...</Typography>
        </Stack>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 420 }}>
          <Stack spacing={2}>
            <Typography variant="h5" component="h1">
              Unable to load pricing
            </Typography>
            <Typography color="text.secondary">{error}</Typography>
          </Stack>
        </Paper>
      </Box>
    )
  }

  if (page === 'yt-credits') {
    return <YoutubeCreditsPublicPage me={me} isLoggedIn={isLoggedIn} />
  }

  return <PricingPublicPage me={me} isLoggedIn={isLoggedIn} />
}

export default PublicApp
